import { PERMISSIONS } from "@island/auth-core";
import type { RealtimeEvent } from "@island/realtime-core";
import { useCallback, useEffect, useRef, useState } from "react";

import type { RealtimeClient } from "@island/browser-client";
import type {
  AgentConversationDto,
  AgentNoteDto,
  MessageDto,
  MessageListResponse,
  NoteListResponse,
} from "../api/types.js";
import { useSession } from "../session/SessionContext.js";
import { Composer } from "./Composer.js";
import { SuggestedReply, type Suggestion } from "./SuggestedReply.js";
import { CustomerPanel } from "./CustomerPanel.js";
import { Transcript } from "./Transcript.js";

/**
 * One conversation.
 *
 * The distinction that shapes this component is **observing versus joining**. Opening
 * a conversation attaches the agent as a silent observer, which the customer cannot
 * detect; joining is a deliberate act that announces them. The UI has to make that
 * difference obvious, because an agent who believes they are invisible while actually
 * being announced will say something they would not have said otherwise.
 */
/**
 * Who is typing, in a word the agent can act on.
 *
 * The socket gateway relays a typing frame with the actor's *type* but no
 * name — deliberately, because it does not hold one: a name would mean a
 * lookup per keystroke on the busiest frame in the protocol. So the best the
 * dashboard could say was "Someone is typing…", which is exactly the moment an
 * agent wants to know **which** someone.
 *
 * The conversation already on screen knows. The assistant sends its own name;
 * a customer is named by the details they gave before the chat started; and a
 * colleague is identified as one, so an agent is never left wondering whether
 * the customer or another agent is about to speak.
 */
function typingActorName(
  event: Extract<RealtimeEvent, { type: "typing" }>,
  conversation: AgentConversationDto | null,
): string {
  // The assistant is the one actor that names itself.
  if (event.displayName !== undefined && event.displayName !== "") {
    return event.displayName;
  }

  if (event.actorType === "customer" || event.actorType === "guest") {
    const stored = conversation?.requesterDisplayName?.trim() ?? "";

    if (stored !== "") return stored;

    const first = conversation?.preChat?.firstName.trim() ?? "";

    return first === "" ? "The customer" : first;
  }

  // Another agent, observing or joined. Named as staff rather than as the
  // customer, which is the distinction that matters before replying.
  if (event.actorType === "agent") return "A colleague";

  return "Someone";
}

/**
 * Whether the customer is still here, in the words an agent needs.
 *
 * Deliberately vague past the first hour. A support tool showing "last seen
 * 14:32:07" invites an agent to reason about someone's exact movements, and the
 * only decision this actually informs is "is it worth typing a long reply right
 * now" — for which "an hour ago" is as useful as a timestamp and rather less
 * uncomfortable.
 */
function presenceLabel(presence: Presence | null): string | null {
  if (presence === null) return null;
  if (presence.online) return "Online";
  if (presence.lastSeenAt === null) return "Offline";

  const minutes = Math.round((Date.now() - new Date(presence.lastSeenAt).getTime()) / 60_000);

  // Negative means the server clock is ahead of this browser's. Reported as
  // just-left rather than "last seen in 3 minutes".
  if (minutes < 1) return "Left just now";
  if (minutes < 60) return `Left ${String(minutes)} min ago`;

  const hours = Math.round(minutes / 60);

  if (hours < 24) return `Left ${String(hours)} ${hours === 1 ? "hour" : "hours"} ago`;

  return `Last seen ${new Date(presence.lastSeenAt).toLocaleDateString()}`;
}

interface Presence {
  readonly online: boolean;
  readonly lastSeenAt: string | null;
}

/**
 * What to call the customer, as a name.
 *
 * Their full name where it is known: the account's, or the one they typed before
 * starting. "Customer" only when there is genuinely nothing — which, since names
 * started being captured, means a visitor who gave none.
 */
function requesterName(conversation: AgentConversationDto | null): string {
  const stored = conversation?.requesterDisplayName?.trim() ?? "";

  if (stored !== "") return stored;

  const preChat = conversation?.preChat;

  if (preChat !== undefined) {
    const typed = `${preChat.firstName} ${preChat.lastName}`.trim();
    if (typed !== "") return typed;
  }

  return conversation?.requester.type === "guest" ? "Guest" : "Customer";
}

/**
 * The same person, inside a sentence.
 *
 * Kept apart from the name because the two read differently: a bubble is labelled
 * "Ted Muyano", and a confirmation asks about "the conversation with Ted" — or,
 * with no name at all, "with this customer", which a bare "Customer" cannot stand
 * in for without the sentence falling apart.
 */
function requesterLabel(conversation: AgentConversationDto | null): string {
  const stored = conversation?.requesterDisplayName?.trim() ?? "";

  if (stored !== "") return stored;

  const first = conversation?.preChat?.firstName.trim() ?? "";

  return first === "" ? "this customer" : first;
}

export function ConversationView({
  conversationId,
  realtime,
  revision,
  typingEvent,
  presenceEvent,
  onChanged,
  onBack,
}: {
  readonly conversationId: string;
  readonly realtime: RealtimeClient | null;
  readonly revision: number;
  /** The most recent typing event for this conversation, forwarded by the shell. */
  readonly typingEvent: Extract<RealtimeEvent, { type: "typing" }> | null;
  /** The most recent presence change for this conversation, forwarded by the shell. */
  readonly presenceEvent: Extract<RealtimeEvent, { type: "presence" }> | null;
  readonly onChanged: () => void;
  /**
   * Back to the queue, on a screen too narrow to show both.
   *
   * A prop rather than a `navigate` call here: this component does not otherwise
   * know it is in a router, and the shell already owns every other route change.
   */
  readonly onBack: () => void;
}) {
  const { api, principal, can } = useSession();

  const [conversation, setConversation] = useState<AgentConversationDto | null>(null);
  const [messages, setMessages] = useState<readonly MessageDto[]>([]);
  const [notes, setNotes] = useState<readonly AgentNoteDto[]>([]);
  const [typingActors, setTypingActors] = useState<readonly string[]>([]);
  /**
   * Whether the customer is still on the site.
   *
   * Null until the first answer arrives, and rendered as nothing rather than as
   * "Offline" — claiming someone has left when the truth is not yet known is worse
   * than saying nothing, because an agent would act on it.
   */
  const [presence, setPresence] = useState<Presence | null>(null);
  /** A drafted reply waiting on the agent. Null when there is nothing to offer. */
  const [suggestion, setSuggestion] = useState<Suggestion | null>(null);
  /** Text handed to the composer by "Edit", cleared as soon as it is taken. */
  const [prefill, setPrefill] = useState<string | null>(null);
  /**
   * The customer message a draft has already been requested for.
   *
   * Without it the effect below would ask again on every re-render — and every ask
   * is a generation on a model that takes seconds.
   */
  const draftedFor = useRef<string | null>(null);
  /**
   * How many live presence events have been applied.
   *
   * The opening fetch and the socket race each other, and the fetch is the slower
   * of the two. Without this, a customer who connected while that request was in
   * flight was shown as offline — the answer had already been overtaken by the time
   * it arrived, and it still won.
   *
   * A counter rather than a timestamp, because both can happen inside the same
   * millisecond and a clock cannot tell that apart. Counting cannot tie.
   */
  const presenceEventsApplied = useRef(0);
  /**
   * The conversation, readable from the typing effect without being a
   * dependency of it.
   *
   * Naming the typist needs the conversation, but re-running that effect every
   * time the conversation is refetched would restart the indicator's six-second
   * timer on an unrelated change — and a refetch happens on every message.
   */
  const conversationRef = useRef<AgentConversationDto | null>(null);
  const [busy, setBusy] = useState(false);
  /** The attachment being looked at, if any. */
  const [viewing, setViewing] = useState<{ src: string; alt: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const agentId = principal?.type === "agent" ? principal.agentId : null;

  /**
   * Whether *this* agent has joined — not whether anyone has.
   *
   * A colleague joining must leave this agent observing silently. Testing the
   * participant list alone would tell an observer they were visible to the customer
   * when they are not, which is the exact confusion the banner exists to remove.
   */
  const joined =
    conversation !== null && agentId !== null && conversation.participantAgentIds.includes(agentId);

  /** Erasing a conversation is an administrator's call, not an agent's. */
  const canDelete = can(PERMISSIONS.SUPPORT_CONVERSATIONS_DELETE);

  const load = useCallback(async () => {
    setError(null);

    try {
      const [detail, history, noteList] = await Promise.all([
        api.get<AgentConversationDto>(`/support/conversations/${conversationId}`),
        api.get<MessageListResponse>(`/conversations/${conversationId}/messages?limit=100`),
        api.get<NoteListResponse>(`/support/conversations/${conversationId}/notes`),
      ]);

      setConversation(detail);
      // Kept in step for the typing indicator, which reads it without depending
      // on it.
      conversationRef.current = detail;
      // History arrives newest-first, which is right for pagination and wrong for
      // reading. Reversed once here rather than in the renderer.
      setMessages([...history.messages].reverse());
      setNotes(noteList.notes);
    } catch (cause) {
      // The status makes the difference between "the platform blinked" and
      // "you are no longer signed in", and an agent reading the message
      // deserves to know which.
      const status =
        typeof cause === "object" && cause !== null && "status" in cause ? Number(cause.status) : 0;

      setError(
        status === 404
          ? "This conversation no longer exists."
          : status === 403
            ? "You do not have access to this conversation."
            : "This conversation could not be loaded. Retrying…",
      );
    }
  }, [api, conversationId]);

  useEffect(() => {
    void load();
  }, [load, revision]);

  /**
   * Ask once, on opening, then let events keep it current.
   *
   * The socket only reports *changes*, so a customer who was already sitting on the
   * page before the agent opened it would never produce one — without this first
   * read the indicator would stay blank for exactly the customer who is most
   * obviously present.
   */
  useEffect(() => {
    /*
      An AbortController purely as the cancellation flag. A plain `let cancelled`
      would be narrowed by the compiler to a constant across the await — it cannot
      see that the cleanup below writes to it — and the check would be compiled out.
    */
    const open = new AbortController();
    const appliedWhenAsked = presenceEventsApplied.current;

    void (async () => {
      try {
        const answer = await api.get<Presence>(`/support/conversations/${conversationId}/presence`);

        // Discarded if an event landed while this was in flight: that event is
        // strictly newer than the question this is the answer to.
        if (!open.signal.aborted && presenceEventsApplied.current === appliedWhenAsked) {
          setPresence(answer);
        }
      } catch {
        // Presence is an ornament on a conversation that works without it. A
        // failure here must not disturb the transcript beside it.
      }
    })();

    return () => {
      open.abort();
    };
  }, [api, conversationId, revision]);

  /**
   * Draft a reply when the customer says something.
   *
   * Asked for here, by the agent's own browser, rather than generated on the server
   * for every conversation. Generation takes seconds on this deployment, and a
   * conversation nobody has open needs no suggestion at all — so the work happens
   * only where somebody is actually looking, and only where they have joined and
   * could send it.
   *
   * Keyed on the last customer message. A newer one replaces the draft, because the
   * old one answered a question that has moved on; anything else the agent does —
   * including nothing — leaves it alone.
   */
  useEffect(() => {
    // `joined` already implies a loaded conversation — it is computed from one.
    if (!joined || conversation.status === "closed") return;

    const last = [...messages].reverse().find((message) => message.messageType !== "system");

    if (
      last === undefined ||
      (last.senderType !== "customer" && last.senderType !== "guest") ||
      last.text.trim() === ""
    ) {
      return;
    }

    if (draftedFor.current === last.messageId) return;

    draftedFor.current = last.messageId;

    const open = new AbortController();

    void (async () => {
      try {
        const drafted = await api.post<{
          draft: string;
          blockedForCustomer?: string;
        }>(`/support/conversations/${conversationId}/draft`, { message: last.text });

        if (open.signal.aborted || drafted.draft.trim() === "") return;

        setSuggestion({
          text: drafted.draft,
          forMessageId: last.messageId,
          ...(drafted.blockedForCustomer !== undefined
            ? { blockedForCustomer: drafted.blockedForCustomer }
            : {}),
        });
      } catch {
        /*
          The assistant is unreachable, or refused. Silent on purpose: this is an
          offer, and an agent who never asked for it should not be handed an error
          about a thing they were not waiting for.
        */
      }
    })();

    return () => {
      open.abort();
    };
  }, [api, conversationId, joined, messages, conversation]);

  /**
   * Only the requester's presence is shown.
   *
   * The gateway also announces colleagues who have joined, and reporting one of
   * those as "Online" beside the customer's name would be a plain lie about who is
   * reading.
   */
  useEffect(() => {
    if (presenceEvent === null) return;
    if (presenceEvent.actorType === "agent" || presenceEvent.actorType === "ai") return;

    presenceEventsApplied.current += 1;
    setPresence({
      online: presenceEvent.online,
      lastSeenAt: presenceEvent.occurredAt,
    });
  }, [presenceEvent]);

  /**
   * Heal a failed load without anybody pressing refresh.
   *
   * This panel used to catch a failure, show a message and stop. A blip — a
   * deploy, a dropped connection, a laptop waking — left a dead conversation on
   * screen that stayed dead after the platform recovered, and the only way out
   * was reloading the page. For a tool somebody sits in front of all day that
   * is the wrong answer to a five-second problem.
   *
   * Only while in error: a healthy conversation is driven by events and does
   * not need a timer.
   */
  useEffect(() => {
    if (error === null) return;

    const retry = setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, 5_000);

    const onWake = (): void => {
      if (document.visibilityState === "visible") void load();
    };

    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);

    return () => {
      clearInterval(retry);
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
    };
  }, [error, load]);

  /**
   * Attach as a silent observer while the conversation is open on screen.
   *
   * Deliberately not a join: an agent reading the queue must not announce themselves
   * to every customer whose conversation they glance at.
   */
  useEffect(() => {
    realtime?.subscribe(conversationId);
    void api.post(`/support/conversations/${conversationId}/observe`).catch(() => {
      // Observation is best-effort. Failing to attach costs live updates, not
      // access, and an error banner here would be noise.
    });

    return () => {
      realtime?.unsubscribe(conversationId);
      void api.delete(`/support/conversations/${conversationId}/observe`).catch(() => {
        // Same: detaching is cleanup, and the observer expires anyway.
      });
    };
  }, [api, realtime, conversationId]);

  /**
   * Ephemeral state driven by live events.
   *
   * Typing is the only thing here that must *not* survive a refetch — everything
   * else is reloaded from the server, which is the authority. A typing indicator
   * that outlived its sender would sit on screen for ever.
   */
  useEffect(() => {
    if (typingEvent === null) return;
    // Our own frames come back to us. "A colleague is typing…" while you are the
    // one typing is a small absurdity, and it hides the customer's indicator
    // behind it.
    if (agentId !== null && typingEvent.actorId === agentId) return;

    const name = typingActorName(typingEvent, conversationRef.current);

    setTypingActors((current) =>
      typingEvent.active
        ? [...new Set([...current, name])]
        : current.filter((actor) => actor !== name),
    );

    // Cleared on a timer as well as on the stop signal: a client that disconnects
    // mid-word never sends the stop, and the indicator would otherwise stick.
    if (!typingEvent.active) return;

    const timer = setTimeout(() => {
      setTypingActors((current) => current.filter((actor) => actor !== name));
    }, 6_000);

    return () => {
      clearTimeout(timer);
    };
  }, [typingEvent]);

  /**
   * Reporting that this agent is typing.
   *
   * Memoised, and that is not a micro-optimisation — it is the fix for the
   * customer never seeing the indicator at all. An inline arrow here is a new
   * function on every render, which made it a changing dependency of the
   * Composer's cleanup effect; React therefore ran that cleanup on *every*
   * render, and the cleanup sends `typing(false)`. Every re-render — including
   * the ones caused by the typing events themselves — cancelled the indicator
   * microseconds after it was raised, so the customer saw nothing while the
   * socket carried a burst of stop frames.
   */
  const reportTyping = useCallback(
    (active: boolean) => {
      realtime?.typing(conversationId, active);
    },
    [realtime, conversationId],
  );

  async function act(action: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setError(null);

    try {
      await action();
      await load();
      onChanged();
    } catch {
      setError("That did not work. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function send(
    text: string,
    productIds: readonly string[] = [],
    attachmentIds: readonly string[] = [],
  ): Promise<void> {
    const created = await api.post<MessageDto>(`/conversations/${conversationId}/messages`, {
      text,
      // Ids only. The API resolves each against the catalogue and builds the card, so
      // the dashboard can never put a price of its own choosing into a transcript.
      ...(productIds.length > 0 ? { productIds: [...productIds] } : {}),
      ...(attachmentIds.length > 0 ? { attachmentIds: [...attachmentIds] } : {}),
    });

    // Appended immediately rather than waiting for the socket to echo it back. The
    // agent typed it, so showing it at once is honest, and the event that follows is
    // reconciled by id.
    setMessages((current) =>
      current.some((message) => message.messageId === created.messageId)
        ? current
        : [...current, created],
    );
  }

  /**
   * Ask for an upload slot, then push the bytes.
   *
   * Two steps by design: the intent call validates type and size for the cost of one
   * small JSON request, so a file of the wrong kind is refused before a byte of it
   * travels. The bytes go to the realtime service, which owns VPS storage — the URL
   * comes back from the intent rather than being built here.
   */
  async function upload(
    file: File,
  ): Promise<{ attachmentId: string; uploadUrl: string | null } | null> {
    try {
      const intent = await api.post<{ attachmentId: string; uploadUrl: string | null }>(
        `/conversations/${conversationId}/attachments/intent`,
        { fileName: file.name, mimeType: file.type, sizeBytes: file.size },
      );

      if (intent.uploadUrl === null) return null;

      const response = await fetch(intent.uploadUrl, {
        method: "PUT",
        credentials: "include",
        headers: { "content-type": file.type },
        body: file,
      });

      if (!response.ok) return null;

      return intent;
    } catch {
      return null;
    }
  }

  const lastSeenRef = useRef<string | null>(null);

  useEffect(() => {
    const newest = messages.at(-1);
    if (newest === undefined || newest.messageId === lastSeenRef.current) return;

    lastSeenRef.current = newest.messageId;
    realtime?.markSeen(conversationId);
  }, [messages, realtime, conversationId]);

  if (error !== null && conversation === null) {
    return (
      <div className="centered">
        <p className="error">{error}</p>
        {/* The retry above runs on its own; this is for somebody who would
            rather not wait the five seconds. */}
        <button
          type="button"
          className="primary"
          onClick={() => {
            void load();
          }}
        >
          Try again
        </button>
      </div>
    );
  }

  if (conversation === null) {
    return (
      <div className="centered">
        <p className="muted">Loading…</p>
      </div>
    );
  }

  const closed = conversation.status === "closed";

  return (
    <div className="conversation">
      <div className="transcript">
        <div className="panel-header">
          {/*
            Hidden on a wide screen by the stylesheet, where the queue is already
            beside this and there is nothing to go back to.
          */}
          <button className="back-to-queue" onClick={onBack} aria-label="Back to the queue">
            ‹ Queue
          </button>

          {/*
            The observing/joined distinction, stated plainly. An agent who thinks
            they are invisible while actually being announced is the failure this
            banner exists to prevent.
          */}
          {joined ? (
            <span className="tag live">You have joined — the customer can see you</span>
          ) : (
            <span className="tag">Observing silently — the customer cannot see you</span>
          )}

          {presenceLabel(presence) !== null && (
            <span
              className={presence?.online === true ? "presence online" : "presence"}
              title={
                presence?.online === true
                  ? "The customer has this chat open"
                  : "The customer is not on the site"
              }
            >
              <span className="presence-dot" aria-hidden="true" />
              {presenceLabel(presence)}
            </span>
          )}

          <span className="spacer" />

          {!joined && !closed && (
            <button
              className="primary"
              disabled={busy}
              onClick={() => {
                void act(async () => api.post(`/support/conversations/${conversationId}/join`));
              }}
            >
              Join conversation
            </button>
          )}

          {joined && (
            <button
              disabled={busy}
              onClick={() => {
                void act(async () => api.post(`/support/conversations/${conversationId}/leave`));
              }}
            >
              Leave
            </button>
          )}

          {!closed && (
            <button
              className="danger"
              disabled={busy}
              onClick={() => {
                void act(async () => api.post(`/support/conversations/${conversationId}/close`));
              }}
            >
              Close
            </button>
          )}

          {/*
            Administrators only, and last in the row.

            Every other control here is recoverable — a closed conversation
            reopens, a join can be left. This one is not, so it sits furthest
            from the reply box, is styled as the destructive action it is, and
            asks before doing anything.
          */}
          {canDelete && (
            <button
              className="danger"
              disabled={busy}
              title="Permanently delete this conversation"
              onClick={() => {
                const name = requesterLabel(conversation);

                if (
                  !window.confirm(
                    `Permanently delete this conversation with ${name}?\n\n` +
                      "The transcript, the agent notes and any images the customer sent " +
                      "are erased. This cannot be undone.",
                  )
                ) {
                  return;
                }

                void act(async () => {
                  await api.delete(`/support/conversations/${conversationId}`);
                  // Nothing left to show. Tell the shell so the queue drops it.
                  onChanged();
                  window.history.back();
                });
              }}
            >
              Delete
            </button>
          )}
        </div>

        <Transcript
          messages={messages}
          viewerId={agentId}
          requesterName={requesterName(conversation)}
          onViewImage={(src, alt) => {
            setViewing({ src, alt });
          }}
        />

        {viewing !== null && (
          <div
            className="lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={viewing.alt}
            /* The backdrop closes it; the picture does not, or every attempt to
               look closely would dismiss the thing being looked at. */
            onClick={(event) => {
              if (event.target === event.currentTarget) setViewing(null);
            }}
          >
            <button
              type="button"
              className="lightbox-close"
              aria-label="Close image"
              onClick={() => {
                setViewing(null);
              }}
            >
              ✕
            </button>
            <img src={viewing.src} alt={viewing.alt} />
          </div>
        )}

        <div className="typing" aria-live="polite">
          {typingActors.length > 0 && `${typingActors.join(", ")} is typing…`}
        </div>

        {closed ? (
          <p className="muted small" style={{ padding: 14, margin: 0 }}>
            This conversation is closed. Reopen it from the customer's side to continue.
          </p>
        ) : (
          <>
            {/*
              Above the message box, where an agent is already looking, and only
              once they have joined — a suggestion is only useful to somebody who
              can act on it.
            */}
            <SuggestedReply
              suggestion={suggestion}
              busy={busy}
              onSend={() => {
                const text = suggestion?.text ?? "";

                // Cleared first: sending is a round trip, and a card that lingers
                // invites a second press that would send it twice.
                setSuggestion(null);

                void act(async () => {
                  await send(text);
                });
              }}
              onEdit={() => {
                setPrefill(suggestion?.text ?? "");
                setSuggestion(null);
              }}
              onIgnore={() => {
                setSuggestion(null);
              }}
            />

            <Composer
              disabled={busy}
              // Sending without joining would put a message in front of the customer
              // from someone they have not been told is present.
              requiresJoin={!joined}
              onTyping={reportTyping}
              onSend={send}
              onUpload={upload}
              prefill={prefill}
              onPrefillConsumed={() => {
                setPrefill(null);
              }}
            />
          </>
        )}

        {error !== null && (
          <p className="error small" style={{ padding: "0 14px 12px" }}>
            {error}
          </p>
        )}
      </div>

      <CustomerPanel
        conversation={conversation}
        notes={notes}
        onAddNote={async (body) => {
          await api.post(`/support/conversations/${conversationId}/notes`, { body });
          await load();
        }}
      />
    </div>
  );
}
